/**
 * Moon Moon Prefetch System
 * Implements intelligent page prefetching for collections and products
 * Based on Shopify's native prefetch behavior
 */

class MoonMoonPrefetch {
    constructor() {
        this.prefetchedUrls = new Set();
        this.isEnabled = true;
        this.hoverDelay = 100; // ms delay before prefetch
        this.maxPrefetchUrls = 10; // Limit concurrent prefetches
        this.init();
    }

    init() {
        // Only enable on production/preview, not in theme editor
        if (window.Shopify && window.Shopify.designMode) {
            this.isEnabled = false;
            return;
        }

        this.bindEvents();
    }

    bindEvents() {
        // Use event delegation for better performance
        document.addEventListener('mouseover', (e) => {
            const link = e.target.closest('a[href]');
            if (link && this.shouldPrefetch(link.href)) {
                setTimeout(() => this.prefetchUrl(link.href), this.hoverDelay);
            }
        });

        // Prefetch on touch start for mobile
        document.addEventListener('touchstart', (e) => {
            const link = e.target.closest('a[href]');
            if (link && this.shouldPrefetch(link.href)) {
                this.prefetchUrl(link.href);
            }
        }, { passive: true });
    }

    shouldPrefetch(url) {
        if (!this.isEnabled || !url) return false;

        // Don't prefetch if already prefetched
        if (this.prefetchedUrls.has(url)) return false;

        // Don't prefetch if too many concurrent requests
        if (this.prefetchedUrls.size >= this.maxPrefetchUrls) return false;

        // Only prefetch same-origin URLs
        try {
            const urlObj = new URL(url, window.location.origin);
            if (urlObj.origin !== window.location.origin) return false;
        } catch {
            return false;
        }

        // Check if it's a collection or product page
        return this.isCollectionOrProduct(url);
    }

    isCollectionOrProduct(url) {
        const pathname = new URL(url, window.location.origin).pathname;
        
        // Collection patterns: /collections/*, /collections, /pages/collections
        if (pathname.includes('/collections/') || pathname === '/collections') {
            return true;
        }

        // Product patterns: /products/*, /pages/products
        if (pathname.includes('/products/')) {
            return true;
        }

        return false;
    }

    prefetchUrl(url) {
        if (!this.shouldPrefetch(url)) return;

        this.prefetchedUrls.add(url);

        // Create prefetch link
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        link.as = 'document';

        // Add to head
        document.head.appendChild(link);

        // Optional: Remove link after timeout to clean up DOM
        setTimeout(() => {
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
        }, 30000);
    }

    // Public API to disable/enable prefetch
    disable() {
        this.isEnabled = false;
    }

    enable() {
        this.isEnabled = true;
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.mmPrefetch = new MoonMoonPrefetch();
    });
} else {
    window.mmPrefetch = new MoonMoonPrefetch();
}

// Expose class for manual usage
window.MoonMoonPrefetch = MoonMoonPrefetch;